/**
 * Traduz a resposta de erro do servidor no bloco correspondente.
 *
 * A tradução é por CÓDIGO, nunca pelo texto da mensagem: o backend já decide status HTTP
 * casando substring em português (AppointmentController.ts:1457), e repetir isso aqui faria
 * o botão de resolver sumir em silêncio a cada ajuste de redação.
 */
import type { PrerequisiteBlockProps } from '../components/common';
import { isApiError } from '../types/common';
import type { PrerequisiteAction, PrerequisiteCode } from '../types/prerequisite';

interface PrerequisiteRecipe {
  title: string;
  // Sem receita de saída quando quem sabe o destino é o servidor: as pendências de emissão
  // de documento trazem uma ação cada, e é delas que os botões nascem.
  resolve?: {
    label: string;
    permission: string;
    // PENDING_COMPLEMENTARY_EXAMS e HAS_LINKED_TRAININGS apontam para o registro específico
    // que gerou o erro, não para uma listagem fixa — por isso a rota pode depender de um campo
    // do `details`. Quando o campo não vem, a função devolve undefined e nenhum botão é
    // oferecido: é melhor sem botão do que um que leva a lugar nenhum.
    route: string | ((details: Record<string, unknown>) => string | undefined);
  };
}

const RECIPES: Record<PrerequisiteCode, PrerequisiteRecipe> = {
  SLOT_FULL: {
    title: 'Este horário não tem mais vaga',
    resolve: { label: 'Abrir vagas', permission: 'schedule_slot.create', route: '/schedule-slots' },
  },
  PERIOD_CAPACITY_FULL: {
    title: 'Este período está com a capacidade esgotada',
    resolve: {
      label: 'Ampliar capacidade',
      permission: 'schedule_slot.create',
      route: '/schedule-slots',
    },
  },
  SLOT_UNAVAILABLE: {
    title: 'Este horário deixou de estar disponível',
    resolve: {
      label: 'Ver horários da clínica',
      permission: 'schedule_slot.create',
      route: '/schedule-slots',
    },
  },
  SLOT_HAS_APPOINTMENTS: {
    title: 'Este horário tem agendamentos',
    resolve: {
      label: 'Ver agendamentos do horário',
      permission: 'appointment.update',
      route: '/appointments',
    },
  },
  // O botão aqui vem de cada pendência, e não da receita: cada uma se resolve numa tela
  // diferente, e o destino de todas elas chega pronto do servidor.
  DOCUMENT_BLOCKING_PENDENCIES: {
    title: 'Há pendências que impedem a emissão',
  },
  HAS_LINKED_TRAININGS: {
    title: 'Este tipo de treinamento está em uso',
    resolve: {
      label: 'Editar tipo de treinamento',
      permission: 'training-type.update',
      // Não há ação de desativar na listagem — o campo status só é editável no formulário.
      route: (details) => {
        const id = readField(details, 'trainingTypeId');
        return id === undefined ? undefined : `/training-types/${String(id)}/edit`;
      },
    },
  },
  PENDING_COMPLEMENTARY_EXAMS: {
    title: 'Há exames complementares sem laudo anexado',
    resolve: {
      label: 'Ir para o prontuário',
      permission: 'medical-records.update',
      route: (details) => {
        const id = readField(details, 'appointmentId');
        return id === undefined ? undefined : `/medical-consultations/${String(id)}`;
      },
    },
  },
  APPOINTMENT_NOT_CHECKED_IN: {
    title: 'Este agendamento não está pronto para consulta',
    resolve: {
      label: 'Ir para a recepção',
      permission: 'reception.checkin',
      route: '/reception',
    },
  },
};

const DENIED_HINT = 'Peça a um administrador para resolver este item antes de continuar.';

/**
 * O httpClient guarda em `details` o corpo INTEIRO da resposta do servidor
 * (httpClient.ts:252), não o `details` que o backend enviou dentro dela. Por isso o
 * código de pré-requisito e as pendências podem estar tanto em `details.<campo>` quanto
 * em `details.details.<campo>` — aceitar as duas formas é o que faz esta função funcionar
 * com o cliente HTTP real do produto, não só com o formato que o backend documenta.
 */
function readField(details: Record<string, unknown>, field: string): unknown {
  if (details[field] !== undefined) return details[field];
  const nested = details.details;
  if (typeof nested === 'object' && nested !== null) {
    return (nested as Record<string, unknown>)[field];
  }
  return undefined;
}

function extractItems(details: Record<string, unknown>): string[] | undefined {
  const pendencies = readField(details, 'pendencies');
  if (!Array.isArray(pendencies)) return undefined;
  // `message` é o campo do `Pendency` do backend (document.ts:58) — não `description`.
  return pendencies
    .map((p) => (typeof p === 'object' && p !== null ? String((p as { message?: unknown }).message ?? '') : ''))
    .filter(Boolean);
}

/**
 * A ação de UMA pendência, conferida campo a campo: o que chega aqui é corpo de resposta
 * HTTP, e uma ação sem rota viraria botão que não leva a lugar nenhum.
 */
function readAction(pendency: unknown): PrerequisiteAction | undefined {
  if (typeof pendency !== 'object' || pendency === null) return undefined;

  const { action } = pendency as { action?: unknown };
  if (typeof action !== 'object' || action === null) return undefined;

  const { route, label, requiredPermission } = action as Record<string, unknown>;
  if (typeof route !== 'string' || route === '') return undefined;
  if (typeof label !== 'string' || label === '') return undefined;
  if (typeof requiredPermission !== 'string' || requiredPermission === '') return undefined;

  return { route, label, requiredPermission };
}

/**
 * Um botão por DESTINO, e não um por pendência: duas pendências que se resolvem na mesma
 * tela viram um botão só, e duas que se resolvem em telas diferentes viram dois. O botão
 * único de antes apontava para uma rota escrita aqui, que não correspondia a tela nenhuma.
 */
function extractActions(details: Record<string, unknown>): PrerequisiteAction[] | undefined {
  const pendencies = readField(details, 'pendencies');
  if (!Array.isArray(pendencies)) return undefined;

  const porDestino = new Map<string, PrerequisiteAction>();
  for (const pendency of pendencies) {
    const action = readAction(pendency);
    if (action && !porDestino.has(action.route)) porDestino.set(action.route, action);
  }

  return porDestino.size > 0 ? [...porDestino.values()] : undefined;
}

export function prerequisiteFromError(error: unknown): PrerequisiteBlockProps | null {
  if (!isApiError(error)) return null;

  const details = error.details;
  if (!details) return null;

  const code = readField(details, 'prerequisite') as PrerequisiteCode | undefined;
  if (!code || !(code in RECIPES)) return null;

  const recipe = RECIPES[code];
  const receita = recipe.resolve;
  const route = typeof receita?.route === 'function' ? receita.route(details) : receita?.route;
  return {
    title: recipe.title,
    reason: error.message || recipe.title,
    items: extractItems(details),
    actions: extractActions(details),
    // O recado explica um botão que foi retido por falta de permissão; sem receita de botão
    // não há o que explicar, e cada ação do servidor traz o recado dela.
    deniedHint: receita ? DENIED_HINT : undefined,
    resolve:
      receita && route
        ? {
            label: receita.label,
            permission: receita.permission,
            mode: 'navigate',
            route,
          }
        : undefined,
  };
}
